import clsx from 'clsx';
import TextWithTooltip from 'components/ui/TextWithTooltip/TextWithTooltip';
import { emptyDisplayValue } from 'constants/common';
import { projectsRoutes } from 'constants/navigation';
import {
  ProjectOrderField,
  ProjectStatus,
  ProjectTableItemFragment,
} from 'generated/graphql';
import { addDate, dateWithoutTimezone } from 'helpers/date';
import { formatProjectDate } from 'helpers/project';
import {
  Tag,
  TagColor,
  TagSize,
} from '@spotted-zebra-uk/ui-components';
import ProjectCandidatesCell from './components/ProjectCandidatesCell/ProjectCandidatesCell';
import {
  FILTER_FIELD_COUNTERS,
  FILTER_RANGE_PAIRS,
  FilterFieldName,
} from './components/ProjectsFilterModal/ProjectsFilterModal.constants';
import ProjectStagesCell from './components/ProjectStagesCell/ProjectStagesCell';
import { getStageCategoryCounts } from './components/ProjectStagesCell/ProjectStagesCell.helpers';
import styles from './Projects.module.scss';

export const DEFAULT_TABLE_PAGE_SIZE = 20;

export const tableHeaders = [
  {
    key: ProjectOrderField.ProjectName,
    header: 'Name',
  },
  {
    key: ProjectOrderField.CompanyName,
    header: 'Company name',
  },
  {
    key: ProjectOrderField.ProjectStages,
    header: 'Stages',
  },
  {
    key: ProjectOrderField.ProjectCandidatesCount,
    header: 'Candidates',
  },
  {
    key: ProjectOrderField.ProjectCreatedAt,
    header: 'Created',
  },
  {
    key: ProjectOrderField.ProjectPublishedAt,
    header: 'Published',
  },
  {
    key: ProjectOrderField.Status,
    header: 'Status',
  },
  {
    key: 'archived',
    header: '',
  },
];

const projectStatusInfo = {
  [ProjectStatus.Draft]: {
    label: 'Draft',
    color: TagColor.YELLOW_BACK,
    disabled: false,
  },
  [ProjectStatus.Review]: {
    label: 'In review',
    color: TagColor.PURPLE,
    disabled: false,
  },
  [ProjectStatus.Published]: {
    label: 'Published',
    color: TagColor.GREEN,
    disabled: false,
  },
  [ProjectStatus.Closed]: {
    label: 'Closed',
    color: TagColor.DISABLED,
    disabled: true,
  },
};

export function parseTableData(project: ProjectTableItemFragment) {
  const statusInfo =
    projectStatusInfo[project.status] || projectStatusInfo[ProjectStatus.Draft];

  return {
    id: project.id.toString(),
    [ProjectOrderField.ProjectName]: <TextWithTooltip text={project.name} />,
    [ProjectOrderField.CompanyName]: (
      <TextWithTooltip text={project.company?.name || ''} />
    ),
    [ProjectOrderField.ProjectStages]: (
      <ProjectStagesCell project={project} />
    ),
    [ProjectOrderField.ProjectCandidatesCount]: (
      <ProjectCandidatesCell
        candidatesCount={project.projectCandidatesCount}
        projectId={project.id}
        // The kinds of stage the role has, so the tooltip can show one nobody
        // is at as a zero — the breakdown query returns no row for those.
        roleStageCategories={getStageCategoryCounts(project).map(
          stageCount => stageCount.category
        )}
      />
    ),
    [ProjectOrderField.ProjectCreatedAt]: formatProjectDate(project.createdAt),
    [ProjectOrderField.ProjectPublishedAt]: project.publishedAt
      ? formatProjectDate(project.publishedAt)
      : emptyDisplayValue,
    [ProjectOrderField.ProjectStatus]: (
      <div className={styles.tagCellWrapper}>
        <Tag
          isDismissible={false}
          isPresentational
          tagColor={statusInfo.color}
          size={TagSize.LARGE}
          isDisabled={statusInfo.disabled}
          containerClass={clsx(statusInfo.disabled && styles.disabledStatusTag)}
        >
          {statusInfo.label}
        </Tag>
      </div>
    ),
    [ProjectOrderField.Status]: (
      <div className={styles.tagCellWrapper}>
        <Tag
          isDismissible={false}
          isPresentational
          tagColor={statusInfo.color}
          size={TagSize.LARGE}
          isDisabled={statusInfo.disabled}
          containerClass={clsx(statusInfo.disabled && styles.disabledStatusTag)}
        >
          {statusInfo.label}
        </Tag>
      </div>
    ),
    archived: (project.isArchived || Boolean(project.deletedAt)) && (
      <div className={styles.archivedTag}>
        <Tag
          isDismissible={false}
          isPresentational
          size={TagSize.LARGE}
          tagColor={TagColor.BLUE}
        >
          Archived
        </Tag>
      </div>
    ),
    linkComponentProps: {
      to: projectsRoutes.editProjectView.url(project.id),
    },
  };
}

/**
 * Filter values vary by field — chips, a segmented control, a text input, the
 * stage rows — so the generic check below narrows rather than enumerating them.
 * Fields it cannot judge declare a counter in FILTER_FIELD_COUNTERS.
 */
type FilterValue = unknown;

const isFilterSet = (value: FilterValue): boolean => {
  if (Array.isArray(value)) {
    return value.length > 0;
  }
  if (typeof value === 'object' && value !== null && 'value' in value) {
    return Boolean((value as { value?: unknown }).value);
  }
  // Covers booleans and the text inputs. "0" is a string, so a From of 0
  // counts as a filter rather than reading as empty.
  return Boolean(value);
};

/**
 * How many filters are active. Pass `fieldNames` to count one tab's worth;
 * omit it for the total shown on the Filter button.
 *
 * A From/To pair counts once however many ends are filled in.
 */
export function getFiltersCount(
  filters: Record<string, FilterValue>,
  fieldNames?: FilterFieldName[],
) {
  const countedFieldNames = fieldNames ?? Object.keys(filters);
  const countedFieldNameSet = new Set<string>(countedFieldNames);
  const pairedFieldNames = new Set<string>();
  let count = 0;

  FILTER_RANGE_PAIRS.forEach(([fromFieldName, toFieldName]) => {
    if (
      !countedFieldNameSet.has(fromFieldName) &&
      !countedFieldNameSet.has(toFieldName)
    ) {
      return;
    }
    pairedFieldNames.add(fromFieldName);
    pairedFieldNames.add(toFieldName);

    if (isFilterSet(filters[fromFieldName]) || isFilterSet(filters[toFieldName])) {
      count += 1;
    }
  });

  countedFieldNames.forEach((fieldName) => {
    if (pairedFieldNames.has(fieldName)) return;

    // A few fields are not simply set or unset — an "Any" segmented control
    // reads as set, and the stage rows are several filters in one field.
    const countField = FILTER_FIELD_COUNTERS[fieldName as FilterFieldName];
    if (countField) {
      count += countField(filters[fieldName] as never);
      return;
    }

    if (isFilterSet(filters[fieldName])) count += 1;
  });

  return count;
}

export function getFiltersButtonAriaLabel(filters: Record<string, FilterValue>) {
  const filtersCount = getFiltersCount(filters);

  if (!filtersCount) {
    return 'Configure filter options';
  }

  if (filtersCount === 1) {
    return 'One filter applied, modify filters';
  }

  return `${filtersCount} filters applied, modify filters`;
}

export function parseToDateFilter(value: string | null) {
  if (!value) {
    return undefined;
  }

  return dateWithoutTimezone(
    addDate(new Date(value), { hours: 23, minutes: 59 }),
  );
}

export function parseFromDateFilter(value: string | null) {
  if (!value) {
    return undefined;
  }

  return dateWithoutTimezone(new Date(value));
}

/**
 * The ATS link filter is applied by an external service, so it is the one
 * filter that can fail on its own. Saying which filter failed is what lets
 * someone clear it, rather than assuming the whole page is broken.
 */
export function getProjectsErrorTitle(error: { message?: string }) {
  return error.message?.includes('ATS')
    ? 'Unable to apply the ATS link filter'
    : 'Unable to get projects';
}
